import type { Spread, Tier } from "@/types/spread";
import { spreadUid } from "@/types/spread";
import type {
  Direction,
  MarketFilter,
  RowMeta,
  SortColumn,
  SortDir,
} from "@/stores/spreads";
import type { DerivedRow, TierBucket } from "./types";
import { searchRank, type SearchRank } from "@/lib/search-rank";

export const TOP_ROUTES_PER_SYMBOL = 1;

export interface PipelineFilters {
  search: string;
  dir: Direction;
  market: MarketFilter;
  hedgeOnly: boolean;
  maxAgeMin: number | null;
  hideBlocked: boolean;
  exchanges: string[];
  excludedExchanges: string[];
  // Subset of tiers to show. Empty array = show all — lets the operator
  // gate on magnitude (e.g. "only Very High + High") by toggling chips
  // in the filter bar.
  tiers: Tier[];
  /** Entity-group-level threshold: a symbol group survives if at least
   *  one of its rows has ``net_spread_pct >= minSpreadPct``. ``null``
   *  disables the filter. Drives the cell-click filtering wired in
   *  ``SpreadCell``. */
  minSpreadPct: number | null;
  /** Same semantics as ``minSpreadPct`` but for ``net_profit_usd``. */
  minProfitUsd: number | null;
  /** ``true`` → keep only rows with ``tradfi_group`` set; ``false`` →
   *  drop them. The default (``false``) preserves the crypto-only view
   *  the operator's used to; flipping it on via the FilterBar's
   *  ``TradFi`` button reveals the CL/OIL/HK50/JP225/SPX500 stream
   *  that the backend now ships in the same ``/ws/spreads`` payload. */
  tradfiOnly: boolean;
  /** Subset of asset classes to show (#58). Empty = all. Non-empty keeps
   *  only rows whose ``asset_class`` (crypto when absent) is in the list. */
  assetClasses: string[];
  expandedSymbols: string[];
}

/** Sort state passed alongside ``PipelineFilters``. Lives outside
 *  ``filters`` because sort is a display preference, not a row filter
 *  — ``resetFilters`` does not touch it. */
export interface PipelineSort {
  by: SortColumn;
  dir: SortDir;
}

// DEX detection moved to ``lib/exchanges`` so SignalRow and pipeline
// share a single list (previously duplicated with a "keep in sync"
// comment). ``isDex`` is the local alias used below.
import { isDexExchange as isDex } from "@/lib/exchanges";

function matchesDir(r: Spread, dir: Direction): boolean {
  if (dir === "all") return true;
  const bDex = isDex(r.buy_exchange);
  const sDex = isDex(r.sell_exchange);
  if (dir === "cex-cex") return !bDex && !sDex;
  if (dir === "dex-dex") return bDex && sDex;
  return bDex !== sDex; // cex-dex
}

function matchesMarket(r: Spread, m: MarketFilter): boolean {
  if (m === "all") return true;
  const bf = r.buy_market === "futures";
  const sf = r.sell_market === "futures";
  switch (m) {
    case "spot-spot":
      return !bf && !sf;
    case "fut-fut":
      return bf && sf;
    case "spot-fut":
      return !bf && sf;
    case "fut-spot":
      return bf && !sf;
  }
}

function isHedge(r: Spread): boolean {
  // Hedge = short on futures. The sell leg must be futures; the buy leg
  // can be anything (spot, DEX spot, or futures). Sell-on-spot is not a
  // hedge because you can't go short without a derivative.
  return r.sell_market === "futures";
}

/** Returns the row's search relevance rank, or ``null`` when it doesn't
 *  match. Symbol is the primary key (exact eth > prefix ethfi > substr)
 *  with exchanges + networks as secondary fallback. */
function rankRowSearch(r: Spread, q: string): SearchRank | null {
  if (!q) return 2;
  const secondary = `${r.buy_exchange} ${r.sell_exchange} ${
    r.common_networks?.map((n) => n.n).join(" ") ?? ""
  }`;
  return searchRank(q, r.symbol, secondary);
}

function matchesExchange(
  r: Spread,
  include: Set<string>,
  exclude: Set<string>,
): boolean {
  // Exclude wins: drop any row where either leg is an excluded venue.
  if (exclude.size > 0 && (exclude.has(r.buy_exchange) || exclude.has(r.sell_exchange))) {
    return false;
  }
  if (include.size === 0) return true;
  // 1 selected → its spreads with every other venue (at least one leg = it).
  if (include.size === 1) {
    return include.has(r.buy_exchange) || include.has(r.sell_exchange);
  }
  // ≥2 selected → ONLY spreads BETWEEN the selected venues (both legs in set).
  return include.has(r.buy_exchange) && include.has(r.sell_exchange);
}

export function isBlocked(r: Spread): boolean {
  // Futures-futures pairs settle in USDT on each exchange — no on-chain
  // transfer between legs, so "blocked" by network rails doesn't apply.
  if (r.buy_market === "futures" && r.sell_market === "futures") return false;
  const buyDex = isDex(r.buy_exchange);
  const sellDex = isDex(r.sell_exchange);
  // DEX-DEX: a custom router handles the swap/bridge; the dashboard doesn't
  // gate these on network rails (and the filter would otherwise hide them
  // all as "blocked").
  if (buyDex && sellDex) return false;
  // DEX-CEX where the CEX leg is futures: hold tokens on-chain, short/long
  // CEX FUT with USDT margin — no on-chain transfer needed.
  if ((buyDex && r.sell_market === "futures") || (sellDex && r.buy_market === "futures")) {
    return false;
  }
  // Otherwise (CEX-CEX or DEX-CEX SPOT): need tokens to flow from the
  // buy side to the sell side. ``wd`` = withdrawal from buy exchange,
  // ``dep`` = deposit to sell exchange (backend convention in
  // _enrich_spreads_with_networks). Both must be open.
  const nw = r.common_networks?.[0];
  if (!nw) return true;
  const wdOk = nw.wd ?? false;
  const depOk = nw.dep ?? false;
  return !(wdOk && depOk);
}

function ageMinutes(r: Spread, now: number): number {
  if (!r.first_seen_at) return 0;
  return Math.max(0, Math.floor((now - r.first_seen_at) / 60));
}

export function computeBuckets(
  rowsMap: Map<string, Spread>,
  filters: PipelineFilters,
  metaMap?: Map<string, RowMeta>,
  sort?: PipelineSort,
): TierBucket[] {
  const nowMs = Date.now();
  const nowSec = nowMs / 1000;
  // ALL filtered rows go into a single list — tier-bucketing happens
  // AFTER per-symbol grouping (see below). Prior to 2026-05-29 we
  // bucketed each row into ``byTier[row.tier]`` first and grouped by
  // symbol inside each tier, which meant a token with routes spanning
  // multiple tiers (ESPORTS @ 4 VH + 7 H routes) appeared as TWO
  // separate symbol groups in the table — one under "Very High", one
  // under "High". The new shape: group ALL routes for a symbol
  // globally, then place the resulting group under the tier of its
  // best route (so ESPORTS' 11 routes live in ONE group, placed under
  // "Very High" because at least one VH route exists). The badge "X
  // routes" then counts every route the operator could expand to.
  const allRows: DerivedRow[] = [];
  const exchangeSet = new Set(filters.exchanges);
  const excludeSet = new Set(filters.excludedExchanges);
  const expandedSet = new Set(filters.expandedSymbols);
  const tierSet = new Set(filters.tiers);  // empty → every tier allowed
  const acSet = new Set(filters.assetClasses);  // empty → every asset class allowed

  // Per-symbol best (lowest) rank — used to sort symbol groups so a
  // ``eth`` query surfaces the ETH bucket before ETHFI within each tier.
  const symRank = new Map<string, SearchRank>();
  for (const r of rowsMap.values()) {
    // TradFi visibility gate. Two paths, exclusive: ``tradfiOnly=true``
    // keeps ONLY rows with ``tradfi_group`` (CL, HK50, JP225, SPX500
    // etc.); the default ``false`` skips those rows so the crypto view
    // doesn't fill with index/commodity spreads the operator may not be
    // hedging. Cheap early continue — placed first so the rest of the
    // filter chain doesn't burn cycles on rows we're about to drop.
    const isTradfi = !!r.tradfi_group;
    if (filters.tradfiOnly ? !isTradfi : isTradfi) continue;
    // Asset-class chips (#58): empty set = all. ``asset_class`` is absent on
    // crypto rows, so default it to "crypto" for the membership test.
    if (acSet.size > 0 && !acSet.has(r.asset_class || "crypto")) continue;
    if (!matchesDir(r, filters.dir)) continue;
    if (!matchesMarket(r, filters.market)) continue;
    if (filters.hedgeOnly && !isHedge(r)) continue;
    const rank = rankRowSearch(r, filters.search);
    if (rank == null) continue;
    if (!matchesExchange(r, exchangeSet, excludeSet)) continue;
    const prevRank = symRank.get(r.symbol);
    if (prevRank == null || rank < prevRank) symRank.set(r.symbol, rank);
    const blocked = isBlocked(r);
    if (filters.hideBlocked && blocked) continue;
    const age = ageMinutes(r, nowSec);
    if (filters.maxAgeMin != null && age > filters.maxAgeMin) continue;
    // Whole-second age too — the Age cell shows seconds while sub-minute
    // ("12s" instead of a flat "<1m"). ``age`` (minutes) still drives the
    // freshness colour, sort, and maxAge filter.
    const ageSec = r.first_seen_at ? Math.max(0, Math.floor(nowSec - r.first_seen_at)) : 0;

    const tier = (r.tier ?? "low") as Tier;
    if (tierSet.size > 0 && !tierSet.has(tier)) continue;
    const uid = spreadUid(r);
    const meta = metaMap?.get(uid);
    const ghost =
      meta && meta.closedAt != null
        ? {
            closedAtMs: meta.closedAt,
            peakSpreadPct: meta.peakSpreadPct,
            livedSec: Math.max(0, Math.floor((meta.closedAt - meta.firstSeenAt) / 1000)),
          }
        : null;
    allRows.push({
      spread: r,
      uid,
      tier,
      symbol: r.symbol,
      symbolFirst: false,
      symbolLast: false,
      symbolRouteCount: 0,
      symbolHiddenCount: 0,
      symbolHiddenActionable: 0,
      symbolHiddenBlocked: 0,
      blocked,
      ageMinutes: age,
      ageSeconds: ageSec,
      ghost,
    });
  }

  // Group ALL filtered rows by symbol — once, globally — so a single
  // token never splits across tier buckets.
  const bySymGlobal = new Map<string, DerivedRow[]>();
  for (const row of allRows) {
    let arr = bySymGlobal.get(row.symbol);
    if (!arr) {
      arr = [];
      bySymGlobal.set(row.symbol, arr);
    }
    arr.push(row);
  }

  // Tier ranking: higher = stronger. A symbol group is placed under
  // the tier of its single best route (so a group with mixed VH/H/Med
  // routes lives under "Very High" — its sub-tier rows still carry
  // their own per-row tier badge via ``DerivedRow.tier``).
  const tierRankMap: Record<Tier, number> = { vh: 3, h: 2, med: 1, low: 0 };
  const groupTier = (rows: DerivedRow[]): Tier => {
    let best: Tier = "low";
    let bestRk = -1;
    for (const r of rows) {
      const rk = tierRankMap[r.tier];
      if (rk > bestRk) {
        bestRk = rk;
        best = r.tier;
      }
    }
    return best;
  };

  const order: Tier[] = ["vh", "h", "med", "low"];
  const sortBy = sort?.by ?? null;
  const sortDir = sort?.dir ?? null;
  const sortActive = sortBy !== null && sortDir !== null;

  /** Shared logic: take a Map of ``symbol → rows in that bucket``,
   *  apply the group-level min-spread / min-profit filter, sort symbol
   *  groups, then materialise into a flat list of ``DerivedRow`` with
   *  expand-state metadata attached to the last visible row of each
   *  group. Used both by the per-tier path (default) and the flattened
   *  cross-tier path (when ``sortActive``). */
  const buildFlat = (bySym: Map<string, DerivedRow[]>): DerivedRow[] => {
    // Group-level threshold filter — see SpreadCell.tsx onClick wiring.
    // A symbol group survives iff at least one of its rows clears BOTH
    // thresholds; ``null`` thresholds are no-op.
    const entries = [...bySym.entries()].filter(([, rows]) => {
      const hasSpread = filters.minSpreadPct == null
        ? true
        : rows.some((r) => r.spread.net_spread_pct >= filters.minSpreadPct!);
      const hasProfit = filters.minProfitUsd == null
        ? true
        : rows.some((r) => r.spread.net_profit_usd >= filters.minProfitUsd!);
      return hasSpread && hasProfit;
    });

    // Symbol sort. Search-relevance always wins (a focused ``eth``
    // query surfaces the ETH bucket first). Within equal rank:
    //  * if the user explicitly clicked a header, use that group-level
    //    max in the requested direction;
    //  * otherwise fall back to legacy default (best profit DESC).
    const syms = entries.sort(([sa, a], [sb, b]) => {
      const ra = symRank.get(sa) ?? 2;
      const rb = symRank.get(sb) ?? 2;
      if (ra !== rb) return ra - rb;
      if (sortBy === "spread" && sortDir) {
        const av = Math.max(...a.map((r) => r.spread.net_spread_pct));
        const bv = Math.max(...b.map((r) => r.spread.net_spread_pct));
        return sortDir === "desc" ? bv - av : av - bv;
      }
      if (sortBy === "profit" && sortDir) {
        const av = Math.max(...a.map((r) => r.spread.net_profit_usd));
        const bv = Math.max(...b.map((r) => r.spread.net_profit_usd));
        return sortDir === "desc" ? bv - av : av - bv;
      }
      const ap = Math.max(...a.map((r) => r.spread.net_profit_usd));
      const bp = Math.max(...b.map((r) => r.spread.net_profit_usd));
      return bp - ap;
    });

    const flat: DerivedRow[] = [];
    for (const [sym, group] of syms) {
      // Within-symbol route sort. Non-blocked rows always lead; the
      // secondary key matches the active column sort so visible top-3
      // routes are actually the top by the column the user is sorting
      // on. Without this consistency the symbol group could be ranked
      // by ``max(spread)`` over ALL 8 routes (including 5 hidden behind
      // the expand toggle), while the 3 visible rows showed
      // top-by-PROFIT — making sort order look "broken" to the user
      // (e.g. an ANTHROPIC group with hidden 60% routes ranks above
      // ECOR @49% but its visible cells show 37%; reported 2026-05-18).
      group.sort((a, b) => {
        if (a.blocked !== b.blocked) return a.blocked ? 1 : -1;
        if (sortBy === "spread" && sortDir) {
          return sortDir === "desc"
            ? b.spread.net_spread_pct - a.spread.net_spread_pct
            : a.spread.net_spread_pct - b.spread.net_spread_pct;
        }
        if (sortBy === "profit" && sortDir) {
          return sortDir === "desc"
            ? b.spread.net_profit_usd - a.spread.net_profit_usd
            : a.spread.net_profit_usd - b.spread.net_profit_usd;
        }
        // No explicit column sort — legacy default (best profit first).
        return b.spread.net_profit_usd - a.spread.net_profit_usd;
      });
      const isExpanded = expandedSet.has(sym);
      const defaultLimit = Math.min(TOP_ROUTES_PER_SYMBOL, group.length);
      const limit = isExpanded ? group.length : defaultLimit;
      const shown = group.slice(0, limit);

      // Stats about the routes that WOULD be hidden in collapsed view,
      // regardless of current expanded state — this keeps the "Hide N
      // routes" label meaningful when the user already expanded.
      const collapsibleRows = group.slice(defaultLimit);
      const hiddenCount = collapsibleRows.length;
      const hiddenActionable = collapsibleRows.reduce(
        (n, r) => (r.blocked ? n : n + 1),
        0,
      );
      const hiddenBlocked = hiddenCount - hiddenActionable;

      shown.forEach((row, idx) => {
        row.symbolFirst = idx === 0;
        row.symbolLast = idx === shown.length - 1;
        row.symbolRouteCount = group.length;
        // Attach expand-bar stats to BOTH the first AND the last
        // visible row of the group when hidden routes exist. The
        // duplication is deliberate: TableView renders a header-style
        // chip on the first row (anchored to the token name) AND a
        // footer chip on the last row (so an operator scrolled deep
        // into an expanded group doesn't have to scroll back up to
        // collapse it). SpreadTable already independently emitted
        // top + bottom expand bars from the last-row stat (see
        // SpreadTable.tsx items useMemo); setting these on the first
        // row too lets per-row consumers (TableView) take the same
        // shape. When the group has a single visible row, first ===
        // last and the chip renders once at that row.
        const here = (row.symbolFirst || row.symbolLast) && hiddenCount > 0;
        row.symbolHiddenCount = here ? hiddenCount : 0;
        row.symbolHiddenActionable = here ? hiddenActionable : 0;
        row.symbolHiddenBlocked = here ? hiddenBlocked : 0;
        flat.push(row);
      });
    }
    return flat;
  };

  // Flattened path: column-sort outranks tier-grouping. When the user
  // explicitly clicked a header to sort by Spread or Profit, the four
  // ``vh / h / med / low`` buckets are merged into one global list so
  // a top-spread "Low" row can actually appear above a worse "Very
  // High" row. The tier on each ``DerivedRow`` is preserved (drives
  // the magnitude color in SpreadCell), only the bucket-level dividers
  // disappear (gated by ``TierBucket.flattened``).
  if (sortActive) {
    if (bySymGlobal.size === 0) return [];
    const flat = buildFlat(bySymGlobal);
    if (flat.length === 0) return [];
    // ``tier`` field of the bucket is irrelevant when ``flattened`` is
    // true (SpreadTable skips the TierDivider), but TS requires a Tier.
    // Pick the tier of the first row so any consumer that bypasses the
    // ``flattened`` flag still gets a vaguely-meaningful value.
    return [{ tier: flat[0].tier, rows: flat, flattened: true }];
  }

  // Per-tier path (no column sort active): split the symbol groups by
  // their BEST-route tier, so a magnitude-classified reading order
  // ``vh → h → med → low`` is preserved at the divider level while
  // each individual token still appears as a single contiguous group
  // with all its routes accessible via expand.
  const bySymByTier: Record<Tier, Map<string, DerivedRow[]>> = {
    vh: new Map(),
    h: new Map(),
    med: new Map(),
    low: new Map(),
  };
  for (const [sym, rows] of bySymGlobal) {
    const t = groupTier(rows);
    bySymByTier[t].set(sym, rows);
  }

  const buckets: TierBucket[] = [];
  for (const tier of order) {
    const bySym = bySymByTier[tier];
    if (bySym.size === 0) continue;
    const flat = buildFlat(bySym);
    if (!flat.length) continue;
    buckets.push({ tier, rows: flat });
  }
  return buckets;
}

export const ALL_EXCHANGES = [
  "ascendex",
  "asterdex",
  "binance",
  "bingx",
  "bitfinex",
  "bitget",
  "bitmart",
  "bybit",
  "coinex",
  "edgex",
  "gateio",
  "hotcoin",
  "htx",
  "hyperliquid",
  "kraken",
  "kucoin",
  "lbank",
  "lighter",
  "mexc",
  "okx",
  "ourbit",
  "phemex",
  "poloniex",
  "weex",
  "xt",
] as const;

export function countByTier(rowsMap: Map<string, Spread>): Record<Tier, number> {
  const out: Record<Tier, number> = { vh: 0, h: 0, med: 0, low: 0 };
  for (const r of rowsMap.values()) {
    const t = (r.tier ?? "low") as Tier;
    out[t] += 1;
  }
  return out;
}

export function countByExchange(rowsMap: Map<string, Spread>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const ex of ALL_EXCHANGES) out[ex] = 0;
  for (const r of rowsMap.values()) {
    if (r.buy_exchange in out) out[r.buy_exchange] += 1;
    if (r.sell_exchange in out) out[r.sell_exchange] += 1;
  }
  return out;
}
